import type { PageHelp } from '../network-help.data';

export const L10N_ru_g1: Record<string, PageHelp> = {
  customers: {
    title: 'Клиенты',
    tag: 'CRM-каталог',
    tagline: 'Главная запись о путешественниках и компаниях, которым продаёт агентство.',
    business: {
      lead: 'Клиенты — это те, кому агентство фактически продаёт: индивидуальные путешественники, корпоративные счета и спутники, которые едут вместе с ними. Ведение одной чистой записи на каждого клиента — это то, что позволяет платформе предзаполнять бронирования, назначать сегменту особую цену, вести клуб лояльности и выставлять счёт нужной стороне. Клиент, которому выставляется счёт, также становится ниже по цепочке контрагентом в учёте (طرف حساب).',
      points: [
        { title: 'Единый источник истины', desc: 'Имя, документ, контакт и согласие хранятся в записи клиента — экраны бронирования читают из неё, поэтому путешественник вводится один раз и переиспользуется повсюду.' },
        { title: 'Сегменты определяют цену', desc: 'Клиент может входить в группу клиентов; тогда слой Retail в Наценках и правилах может назначить этой группе особую цену — скидку для VIP, сбор для случайных клиентов.' },
        { title: 'Согласие фиксируется', desc: 'Согласие на маркетинг и на обработку данных хранится по каждому клиенту, чтобы рассылки оставались законными.' },
      ],
    },
    software: {
      lead: 'Поисковый список всех клиентов со столбцами контактов, лояльности и согласий, нужными для управления ими.',
      points: [
        { title: 'Найти и отфильтровать', desc: 'Поиск по имени, телефону, email или национальному ID; откройте строку для полного профиля.' },
        { title: 'Редактировать профиль', desc: 'Панель редактирования фиксирует личность, телефон/WhatsApp (отдельно код набора и номер), гражданство, предпочтительный канал и язык, VIP-уровень и внутренние заметки; добавьте спутников, которые едут вместе с клиентом.' },
        { title: 'Лояльность с одного взгляда', desc: 'Каждый профиль показывает баланс баллов клиента, уровень и историю с ручным начислением / списанием / корректировкой.' },
      ],
    },
    related: ['customer-groups', 'loyalty', 'markup', 'agencies'],
  },

  'customer-groups': {
    title: 'Группы клиентов',
    tag: 'Сегментация',
    tagline: 'Помечайте клиентов сегментами, чтобы слой розничного ценообразования Retail мог обращаться с ними по-разному.',
    business: {
      lead: 'Группа клиентов — это именованный сегмент («VIP», «Корпоративные — Acme», «Случайные»), к которому можно применять цену. Это клиентский аналог группы агентств: там, где группы агентств задают область правил Wholesale/Distribution, группы клиентов задают область слоя Retail, чтобы продающее агентство могло вознаградить или обложить надбавкой часть своих собственных клиентов.',
      points: [
        { title: 'Рычаг розничной маржи', desc: 'Членство в группе — это то, на что нацелено правило Retail — например, скидка лояльности −3% для группы VIP или сервисный сбор для непроверенных случайных клиентов.' },
        { title: 'Членство, а не иерархия', desc: 'Клиент просто входит в группу; вложенности нет. Одна чёткая метка делает ценообразование предсказуемым.' },
        { title: 'Принадлежит агентству', desc: 'Каждое агентство определяет свои группы для своих клиентов — они не перетекают через сеть.' },
      ],
    },
    software: {
      lead: 'Создавайте группы и назначайте клиентов, затем ссылайтесь на группу из области Group правила розничного ценообразования Retail.',
      points: [
        { title: 'Создать и назвать группу', desc: 'Определите группу с кодом и названием; активируйте или архивируйте её по мере развития сегментов.' },
        { title: 'Назначить участников', desc: 'Добавляйте или удаляйте клиентов из группы; счётчик появляется на группе и на строках правил Наценок и правил с областью Group.' },
        { title: 'Подключить к ценообразованию', desc: 'В Наценках и правилах выберите область партнёра = Group на правиле Retail и укажите эту группу — каждое подходящее бронирование её участников получит это правило.' },
      ],
    },
    examples: [
      {
        title: 'Скидка для группы VIP на слое Retail',
        rows: [
          { label: 'Промежуточный итог путешественника (до правила retail)', value: '11,024,000' },
          { label: 'Retail DISCOUNT −3% · группа = VIP', value: '−330,720', accent: 'success', strong: true },
          { label: 'VIP-клиент платит', value: '10,693,280' },
        ],
        note: 'То же бронирование для не-VIP-клиента пропускает правило и платит 11,024,000. Единственное отличие — членство в группе.',
      },
      {
        title: 'Сервисный сбор для случайного клиента — обратное направление',
        rows: [
          { label: 'Промежуточный итог путешественника', value: '11,024,000' },
          { label: 'Retail SERVICE_FEE +150,000 · группа = Случайные', value: '+150,000', accent: 'accent', strong: true },
          { label: 'Случайный клиент платит', value: '11,174,000' },
        ],
        note: 'Фиксированный SERVICE_FEE облагает надбавкой непроверенный сегмент. Фиксированные правила игнорируют размер тарифа — каждое бронирование случайного клиента добавляет одни и те же 150,000, какой бы ни была цена билета.',
      },
      {
        title: 'Корпоративный нетто-тариф — группа также может открыть договорную сделку',
        rows: [
          { label: 'Стандартный промежуточный итог путешественника', value: '11,024,000' },
          { label: 'Retail DISCOUNT −6% · группа = Corporate · Acme', value: '−661,440', accent: 'success', strong: true },
          { label: 'Сотрудник Acme платит', value: '10,362,560' },
        ],
        note: 'Более глубокая групповая скидка кодирует корпоративный контракт: каждый, помеченный в «Corporate · Acme», автоматически получает договорные 6% скидки Acme — без переопределений по каждому бронированию.',
      },
    ],
    related: ['customers', 'markup', 'agency-groups', 'loyalty'],
  },

  loyalty: {
    title: 'Клуб лояльности',
    tag: 'Voyager Rewards',
    tagline: 'Программа баллов агентства — как путешественники накапливают, поднимаются по уровням и списывают баллы.',
    business: {
      lead: 'Клуб лояльности превращает разовых путешественников в постоянных. Клиенты накапливают баллы при бронировании, поднимаются по уровням, которые открывают привилегии и более высокие ставки начисления, и списывают баллы против будущих бронирований. Непогашенный баланс — это реальное обязательство, которое несёт агентство, поэтому клуб ведётся с явными ставками начисления и списания.',
      points: [
        { title: 'Удержание, измеренное количественно', desc: 'Баллы и уровни дают клиенту повод забронировать снова у вас, а не у конкурента.' },
        { title: 'Уровни по накопленному за всё время', desc: 'Уровень клиента отражает накопленные за всё время баллы, поэтому списание никогда не понижает его — Bronze → Silver → Gold → Platinum, каждый с более высоким множителем начисления.' },
        { title: 'Баллы — это обязательство', desc: 'Каждый несписанный балл — это должные деньги; обзор клуба показывает суммарные баллы и денежное обязательство, чтобы оно оставалось на балансе.' },
      ],
    },
    software: {
      lead: 'Обзор клуба плюс две вещи, которые им управляют: настройки (ставки + триггер) и правила автоначисления.',
      points: [
        { title: 'KPI обзора', desc: 'Участники, суммарные баллы, обязательство и распределение по уровням — вычисляются вживую из журнала.' },
        { title: 'Настройки клуба', desc: 'Задайте ставку начисления, ставку списания (баллы за единицу), срок действия баллов и триггер автоначисления — начислять при ISSUE (выполнено) или при PAID (платёж захвачен).' },
        { title: 'Правила начисления', desc: 'Составляйте правила баллов по продукту (например, Авиабилеты начисляют 2× за единицу); завершение бронирования автоматически начисляет баллы по подходящему правилу. Начисление / списание / корректировка по каждому клиенту находится в каждом профиле.' },
      ],
    },
    modes: {
      lead: 'Существуют две отдельные концепции уровня — не путайте их.',
      modes: [
        { name: 'Loyalty tier', when: 'накоплено', desc: 'Bronze / Silver / Gold / Platinum, выводится из накопленных за всё время баллов. Определяет множитель начисления и привилегии. Никогда не задаётся вручную.' },
        { name: 'VIP tier', when: 'вручную', desc: 'Отдельный флаг, который агент устанавливает в записи клиента (Standard / Silver / Gold / VIP) для обслуживания — не зависит от баллов лояльности.' },
      ],
    },
    examples: [
      {
        title: 'Пороги уровней — задаются накопленными за всё время баллами',
        rows: [
          { label: 'Bronze · 0+ накоплено', value: '1× начисление' },
          { label: 'Silver · 2,000+ накоплено', value: '1.25× начисление' },
          { label: 'Gold · 6,000+ накоплено', value: '1.5× начисление', accent: 'info' },
          { label: 'Platinum · 15,000+ накоплено', value: '2× начисление', accent: 'success' },
        ],
        note: 'Уровень читает накопленное за всё время, поэтому трата баллов никогда не понижает клиента.',
      },
      {
        title: 'Автоначисление при бронировании и скачок уровня',
        rows: [
          { label: 'Накоплено за всё время до (Silver)', value: '5,600' },
          { label: 'Правило Авиабилеты · 2 балла за единицу × 250', value: '+500', accent: 'info', strong: true },
          { label: 'Накоплено за всё время после → Gold', value: '6,100' },
        ],
        note: 'Баллы начисляются автоматически, когда бронирование достигает триггера начисления клуба (ISSUE или PAID). Пересечение 6,000 повышает клиента до Gold — с этого момента начисление 1.5×.',
      },
      {
        title: 'Списание баллов (по умолчанию 100 баллов = 1 единица скидки)',
        rows: [
          { label: 'Баланс баллов', value: '8,000' },
          { label: 'Списать 5,000 баллов → скидка', value: '−50', accent: 'success' },
          { label: 'Остаток баланса', value: '3,000', strong: true },
        ],
        note: 'Ставка списания задаётся в настройках клуба; баланс — это тратимые баллы, отличные от накопленных за всё время, которые задают уровень.',
      },
      {
        title: 'Обязательство клуба — сколько стоят непогашенные баллы',
        rows: [
          { label: 'Всего несписанных баллов у участников', value: '2,400,000' },
          { label: '÷ ставка списания (100 баллов = 1)', value: '÷ 100' },
          { label: 'Денежное обязательство на балансе', value: '24,000', accent: 'accent', strong: true },
        ],
        note: 'Обзор показывает это, чтобы клуб никогда не был скрытой затратой. Повышение ставки списания (больше баллов за единицу) снижает обязательство, но делает баллы менее ценными для клиента — это рычаг для баланса.',
      },
    ],
    related: ['customers', 'customer-groups', 'markup'],
  },

  agencies: {
    title: 'Агентства',
    tag: 'Каталог',
    tagline: 'Главный каталог B2B-туристических компаний, торгующих на платформе.',
    business: {
      lead: 'Агентства — это B2B-клиенты платформы, туристические компании, которым вы продаёте инвентарь. Каждое несёт коммерческие и комплаенс-условия, определяющие, как оно торгует: свой уровень, свой кошелёк или кредитную линию и свой статус KYC.',
      points: [
        { title: 'Уровни задают условия', desc: 'Уровень агентства — это рычаг за его ценообразованием: он питает правила с областью Tier в движке Наценок и правил, поэтому объём можно вознаградить лучшей маржой.' },
        { title: 'Кошелёк и кредит — торговый лимит', desc: 'Агентство бронирует против предоплаченного кошелька или одобренной кредитной линии; этот баланс ограничивает риск платформы.' },
        { title: 'KYC открывает торговлю', desc: 'Новое агентство выходит в работу только после одобрения KYC — столбец статуса показывает по каждой строке, кто торгует, а кто заблокирован.' },
      ],
    },
    software: {
      lead: 'Поисковый список всех агентств с коммерческими и комплаенс-столбцами, нужными для управления ими с одного взгляда.',
      points: [
        { title: 'Просмотреть и отфильтровать', desc: 'Поиск по имени, контакту или коду и фильтрация по уровню или статусу, чтобы найти нужных.' },
        { title: 'Читать столбцы здоровья', desc: 'Баланс кошелька, использование кредита и статус KYC появляются по каждой строке.' },
        { title: 'Открыть запись', desc: 'Кликните строку, чтобы открыть профиль агентства — редактируйте его условия, SIGN, каналы продаж, роли и доступ.' },
      ],
    },
    related: ['agency-profile', 'agents', 'agency-onboarding', 'markup'],
  },

  markup: {
    title: 'Наценки и правила',
    tag: 'Движок ценообразования',
    tagline: 'Место, где определяется каждый слой маржи — от владельца SIGN до реселлера и путешественника.',
    business: {
      lead: 'Так строится цена от нетто-тарифа поставщика до того, что платит путешественник. Движок слоистый: наценка Wholesale (владелец SIGN берёт с реселлера), сбор Distribution (владелец канала берёт с агентства, продающего через его канал) и маржа Retail (собственная наценка продающего агентства для клиента). Каждый слой — это деньги другой стороны.',
      points: [
        { title: 'Три слоя, три отношения', desc: 'Wholesale = владелец SIGN → реселлер; Distribution = владелец канала → продавец; Retail = продавец → путешественник. Правило всегда принадлежит ровно одному слою.' },
        { title: 'Нацеливание по сегменту', desc: 'Правила сужаются по продукту, поставщику, авиакомпании, маршруту, классу салона, SIGN или каналу, уровню или группе партнёра либо конкретному партнёрскому агентству — чтобы назначать цену точно.' },
        { title: 'Ограждения защищают цену', desc: 'Мин/макс-пороги, приоритет и статус активно/неактивно не дают плохому правилу сделать тариф неконкурентным или убыточным.' },
      ],
    },
    software: {
      lead: 'Движок правил, разрешаемый по специфичности — самое специфичное подходящее правило выигрывает внутри каждого слоя.',
      points: [
        { title: 'Порядок специфичности', desc: 'Партнёр → Уровень → SIGN / Канал → Маршрут → Умолчание владельца. Резолвер проходит эту лестницу для каждого слоя бронирования.' },
        { title: 'Оценка — это как резолвер выбирает', desc: 'Каждое неподстановочное измерение совпадения, заданное правилом, добавляет фиксированный вес к его баллу. Победитель по слою = наивысший балл. Ничья → выигрывает более высокий `priority`; всё ещё ничья → выигрывает недавнее обновлённое правило. Симулятор показывает балл на каждой карточке кандидата, чтобы вы точно видели, почему одно правило победило другое.' },
        { title: 'Строить и фильтровать правила', desc: 'Составьте слой правила, тип корректировки, значение (процент / фиксированное / ступенчатое), базу и область; фильтруйте растущий список по типу, продукту, области партнёра или статусу.' },
        { title: 'Симулировать до запуска', desc: 'Встроенный симулятор оценивает гипотетическое бронирование и показывает, какое именно правило срабатывает на каждом слое — без риска для живого ценообразования.' },
      ],
    },
    examples: [
      {
        title: 'Веса специфичности — сколько стоит каждое измерение совпадения',
        rows: [
          { label: 'Партнёр = конкретное AGENCY', value: '100' },
          { label: 'SIGN (wholesale) · Канал (distribution)', value: '90' },
          { label: 'Пункт отправления и назначения — аэропорт / станция', value: '70' },
          { label: 'Партнёрская GROUP', value: '60' },
          { label: 'Пункт отправления и назначения — город', value: '50' },
          { label: 'Партнёрский TIER', value: '40' },
          { label: 'Регион', value: '35' },
          { label: 'Поставщик · Авиакомпания · Тип тарифа', value: '30 каждое' },
          { label: 'Класс салона', value: '25' },
          { label: 'Продукт', value: '20' },
        ],
        note: 'Балл правила — это сумма весов каждого неподстановочного измерения, которое действительно совпало с бронированием. Подстановки (пустые) не дают баллов — они просто не исключают.',
      },
      {
        title: 'Два правила соперничают — выигрывает наивысший балл',
        rows: [
          { label: 'Бронирование: продавец = Nomad (уровень GOLD), продукт = авиа', value: '' },
          { label: 'Правило A · WHOLESALE · TIER=GOLD + продукт=авиа', value: '40 + 20 = 60', accent: 'info' },
          { label: 'Правило B · WHOLESALE · AGENCY=Nomad', value: '100', accent: 'success' },
          { label: 'Победитель', value: 'Правило B', strong: true },
        ],
        note: 'Оба правила подходят этому бронированию. Привязка к партнёрскому агентству у правила B набирает 100 сама по себе — обыгрывает связку уровень+продукт правила A (60). Специфичный по партнёру — самое специфичное измерение, которое знает движок.',
      },
      {
        title: 'Одинаковый балл — приоритет решает ничью',
        rows: [
          { label: 'Бронирование: продукт=авиа, класс=BUSINESS, поставщик=Amadeus', value: '' },
          { label: 'Правило X · продукт+класс (20+25) · priority=0', value: 'балл 45 · pri 0' },
          { label: 'Правило Y · продукт+поставщик (20+30) · priority=10', value: 'балл 50 · pri 10', accent: 'success' },
          { label: 'Победитель', value: 'Правило Y (выше балл)', strong: true },
        ],
        note: 'Y выигрывает по чистому баллу (50 > 45). Если два правила когда-либо сравнялись бы по баллу, `priority` решает ничью — а если `priority` тоже равен, выигрывает недавно обновлённое правило. Этот последний тайбрейк означает, что редактирование существующего правила фактически освежает его до верха иначе-равной группы.',
      },
      {
        title: 'Wholesale — владелец SIGN → реселлер (на нетто-тариф)',
        rows: [
          { label: 'Нетто авиакомпании', value: '10,000,000' },
          { label: 'MARKUP +4% от нетто', value: '+400,000', accent: 'info' },
          { label: 'Себестоимость реселлера', value: '10,400,000', strong: true },
        ],
      },
      {
        title: 'Retail — продавец → путешественник (на текущий брутто)',
        rows: [
          { label: 'Текущий итог', value: '10,400,000' },
          { label: 'MARKUP +6% от брутто', value: '+624,000', accent: 'success' },
          { label: 'Промежуточный итог путешественника', value: '11,024,000', strong: true },
        ],
      },
      {
        title: 'Distribution — сбор канала (надбавка сверху vs доля выручки)',
        rows: [
          { label: 'Надбавка +2% на 11,024,000', value: '+220,480', accent: 'accent' },
          { label: 'Доля выручки 2% (из маржи)', value: 'цена без изменений' },
        ],
        note: 'Надбавка взимается сверх тарифа; доля выручки не трогает цену путешественника и вычитает долю канала из маржи продавца.',
      },
      {
        title: 'Режимы значения — база 10,000,000, бронирование 3 пассажира / 2 сегмента',
        rows: [
          { label: 'Процент · 5% × 10,000,000', value: '500,000' },
          { label: 'Фиксированное · 150,000 за бронирование', value: '150,000' },
          { label: 'Фиксированное · 150,000 за пассажира × 3', value: '450,000' },
          { label: 'Фиксированное · 150,000 за сегмент × 2', value: '300,000' },
          { label: 'Процент + фиксированное · 3% + 200,000', value: '500,000' },
        ],
        note: 'Любой слой может использовать любой режим. Тип корректировки задаёт знак: MARKUP / SERVICE_FEE добавляют; DISCOUNT / REBATE вычитают.',
      },
      {
        title: 'Сквозной каскад — партнёр выписывает под SIGN, затем перепродаёт',
        rows: [
          { label: 'Нетто поставщика (авиакомпания)', value: '10,000,000' },
          { label: '+ Wholesale MARKUP 4%', value: '+400,000', accent: 'info' },
          { label: '+ Retail MARKUP 6%', value: '+624,000', accent: 'success' },
          { label: '+ Distribution надбавка 2%', value: '+220,480', accent: 'accent' },
          { label: 'Цена путешественника', value: '11,244,480', strong: true },
        ],
        note: 'Ровно то, что вычисляет симулятор. При доле выручки вместо надбавки путешественник всё равно платит 11,024,000, а 220,480 канала снимаются с маржи продавца.',
      },
    ],
    related: ['agency-groups', 'sign-access', 'channel-access', 'agencies'],
  },

  'partner-access': {
    title: 'Партнёрский доступ',
    tag: 'Спаривание',
    tagline: 'Как агентства объединяются, чтобы делиться выписными полномочиями (SIGN) и каналами продаж.',
    business: {
      lead: 'Не каждое агентство держит авиакредентиалы (SIGN) или каналы продаж, нужные для оформления всего, что оно может продать. Партнёрский доступ позволяет двум агентствам соединиться и делиться этими активами — чтобы одно могло выписывать на SIGN другого или продавать через его канал — расширяя то, что могут продавать оба, без покупки нового инвентаря.',
      points: [
        { title: 'Заимствуйте охват, сохраняйте контроль', desc: 'Владелец предоставляет доступ и может отозвать его в любой момент — совместное использование это разрешение, а не передача актива.' },
        { title: 'Обе стороны должны согласиться', desc: 'Спаривание и каждое предоставление — это двустороннее согласие: оферта или запрос, который другая сторона принимает, прежде чем любой актив станет общим.' },
        { title: 'Два направления ценности', desc: 'Вы отслеживаете, чем делитесь с партнёрами и что используете от них, как два отдельных журнала.' },
      ],
    },
    software: {
      lead: 'Этот экран управляет спариваниями и предоставлениями SIGN / канала продаж, которые проходят через них.',
      points: [
        { title: 'Соединение по коду агентства', desc: 'Отправьте запрос на партнёрство, используя уникальный код агентства партнёра; он принимает или отклоняет его.' },
        { title: 'Предоставление SIGN и каналов продаж', desc: 'Предложите SIGN (авиавыписной кредентиал) или канал продаж партнёру, с областью и ограничением по сроку; получатель принимает, чтобы активировать его.' },
        { title: 'Управление обоими журналами', desc: 'Просматривайте доступ, который вы предоставили, и доступ, который вы получили, и отзывайте его, когда отношения заканчиваются.' },
      ],
    },
    examples: [
      {
        title: 'Вы делитесь SIGN — вы зарабатываете наценку wholesale на каждом партнёрском билете',
        rows: [
          { label: 'Партнёр выписывает билет EK · нетто авиакомпании', value: '10,000,000' },
          { label: 'Ваша наценка Wholesale 4% (они платят вам)', value: '+400,000', accent: 'info', strong: true },
          { label: 'Себестоимость билета для партнёра', value: '10,400,000' },
        ],
        note: 'Вы никогда не касаетесь бронирования — совместное использование вашего SIGN приносит вам 400,000 за каждый билет, который партнёр выписывает под ним. Он оставляет любую розничную маржу, которую добавляет для своего клиента.',
      },
      {
        title: 'Вы используете SIGN партнёра — платите его wholesale, оставляете свой retail',
        rows: [
          { label: 'Вы выписываете билет EK на их SIGN · нетто', value: '10,000,000' },
          { label: '+ Их наценка Wholesale 4% → ваша себестоимость', value: '10,400,000' },
          { label: '+ Ваша наценка Retail 6% для вашего клиента', value: '+624,000', accent: 'success' },
          { label: 'Ваш клиент платит', value: '11,024,000', strong: true },
        ],
        note: 'Заимствование SIGN позволяет вам продавать авиакомпанию, которую вы не держите. Вы платите наценку wholesale владельца и оставляете свою розничную маржу 624,000.',
      },
      {
        title: 'Вы делитесь каналом продаж — доля выручки vs надбавка сверху',
        rows: [
          { label: 'Партнёр продаёт тариф 10,000,000 через ваш канал', value: '10,000,000' },
          { label: 'Доля выручки 2% (ваша доля, из их маржи)', value: '+200,000', accent: 'accent', strong: true },
          { label: 'Надбавка 2% (взимается сверху → платит путешественник)', value: '10,200,000' },
        ],
        note: 'Доля выручки приносит вам 200,000 из маржи продавца без изменения цены путешественника; надбавка добавляет ваш сбор сверх тарифа. Вы задаёте, что именно, по каждому предоставлению.',
      },
    ],
    related: ['sign-access', 'channel-access', 'signs', 'sale-channels'],
  },

  agents: {
    title: 'Субагентства',
    tag: 'Нижестоящая сеть',
    tagline: 'Реселлеры, которые находятся под мастер-агентством и продают от его имени.',
    business: {
      lead: 'Субагентства расширяют дистрибуцию без того, чтобы платформа подключала каждого напрямую. Мастер-агентство набирает их, задаёт их коммерческие условия и стоит за их торговлей — превращая одни отношения в цепочку продаж.',
      points: [
        { title: 'Охват без накладных расходов', desc: 'Каждое субагентство — это новая точка продаж, которую платформе никогда не пришлось подписывать или финансировать по отдельности.' },
        { title: 'Маржа слоится', desc: 'Мастер-агентство может добавить свою наценку Retail поверх цены платформы, прежде чем субагентство увидит тариф — этот спред и есть его бизнес.' },
        { title: 'Консолидированная подотчётность', desc: 'Кошелёк, кредит и расчёты сворачиваются к родителю, поэтому платформа рассчитывается с одним контрагентом.' },
      ],
    },
    software: {
      lead: 'Перечисляет всех, кто отчитывается перед агентством, с элементами управления тем, что каждый может делать.',
      points: [
        { title: 'Конфигурация по каждому агентству', desc: 'Переопределения наценки, разрешённые продукты и доступ задаются на каждом субагентстве из его записи.' },
        { title: 'Сводные итоги', desc: 'Бронирования и выручка нижестоящей сети агрегируются к родителю и к сетевому итогу.' },
        { title: 'Вид админа vs агентства', desc: 'Админы видят всю сеть; вошедшее агентство видит только свою нижестоящую сеть.' },
      ],
    },
    related: ['agencies', 'markup', 'partner-access'],
  },

  companies: {
    title: 'Компании',
    tag: 'Корпоративный родитель',
    tagline: 'Юридические лица, которые владеют каналами продаж и группируют агентства под одной крышей.',
    business: {
      lead: 'Компания — это корпоративный родитель над агентствами, лицо, которое может владеть каналами продаж и поэтому зарабатывать сборы слоя Distribution. Группировка брендов или агентств под одной компанией держит владение общими коммерческими активами и расчёты в одном месте.',
      points: [
        { title: 'Владеет дистрибуционными активами', desc: 'Компании держат каналы продаж (не SIGN); правила Distribution компании назначают цену тому, что проходит через эти каналы.' },
        { title: 'Один юридический контрагент', desc: 'Несколько агентств или брендов могут находиться под одной компанией для контрактов и расчётов.' },
        { title: 'Разделение обязанностей', desc: 'Авиавыписка (SIGN) живёт у агентств; дистрибуция каналов живёт у компании.' },
      ],
    },
    software: {
      lead: 'Каталог компаний и каналов, которыми они владеют.',
      points: [
        { title: 'Управление записями компаний', desc: 'Создавайте и редактируйте компании и смотрите агентства и каналы, привязанные к каждой.' },
        { title: 'Владение каналом', desc: 'Каналы продаж компании управляются из Каналов продаж и оцениваются из Наценок и правил (слой Distribution).' },
        { title: 'Область в селекторах', desc: 'Владельцы компаний появляются в селекторе владельца Наценок и правил как «только каналы».' },
      ],
    },
    related: ['sale-channels', 'channel-access', 'agencies'],
  },

  'sale-channels': {
    title: 'Каналы продаж',
    tag: 'Дистрибуция',
    tagline: 'Витрины и каналы, через которые продаётся инвентарь.',
    business: {
      lead: 'Канал продаж — это путь на рынок: сайт, приложение, API-интеграция или офлайн-стойка — которым владеет агентство или компания. Это актив, против которого взимает слой Distribution: тот, кто владеет каналом, может брать сбор или долю выручки с того, что продаётся через него.',
      points: [
        { title: 'Канал — это точка выручки', desc: 'Продажи, проходящие через канал, могут нести сбор Distribution или долю выручки для владельца канала.' },
        { title: 'Владеть или заимствовать', desc: 'Вы продаёте через свои каналы или через партнёрский по предоставлению доступа к каналу.' },
        { title: 'Формируйте распределение', desc: 'Сбор канала может быть надбавкой (сверх тарифа) или долей выручки (из маржи продавца).' },
      ],
    },
    software: {
      lead: 'Каталог каналов продаж с их владельцем и формой расчётов.',
      points: [
        { title: 'Создать и настроить', desc: 'Зарегистрируйте канал, задайте его владельца (агентство или компанию) и его долю выручки / форму по умолчанию.' },
        { title: 'Предоставить партнёрам', desc: 'Поделитесь каналом через доступ к каналу, чтобы партнёрское агентство могло продавать через него.' },
        { title: 'Питает ценообразование', desc: 'Правила Distribution в Наценках и правилах нацеливаются на конкретный канал или на все.' },
      ],
    },
    related: ['channel-access', 'companies', 'partner-access', 'markup'],
  },

  'channel-access': {
    title: 'Доступ к каналу',
    tag: 'Предоставление дистрибуции',
    tagline: 'Позволение партнёрскому агентству продавать через ваш канал продаж.',
    business: {
      lead: 'Доступ к каналу — это то, как канал продаж одалживается другому агентству. Владелец предоставляет партнёру право продавать через свой канал — обычно за долю выручки — расширяя дистрибуцию без передачи актива. Это гранулярное предоставление за слоем ценообразования Distribution.',
      points: [
        { title: 'Охват за долю выручки', desc: 'Получатель продаёт через канал; владелец оставляет долю брутто, проходящего через него.' },
        { title: 'Двустороннее согласие', desc: 'Предоставление предлагается и принимается — ничего не разделяется, пока обе стороны не согласятся — и может быть отозвано в любой момент.' },
        { title: 'Управляет правилами Distribution', desc: 'Доля выручки предоставления — это унаследованный запасной вариант; явные правила Distribution в Наценках и правилах переопределяют его.' },
      ],
    },
    software: {
      lead: 'Журнал предоставлений каналов — выданных и полученных.',
      points: [
        { title: 'Предоставить и задать область', desc: 'Предложите канал партнёру с долей выручки, формой и необязательным окном действия.' },
        { title: 'Принять / отклонить', desc: 'Получатель принимает, чтобы активировать доступ, или отклоняет его.' },
        { title: 'Отозвать', desc: 'Прекратите доступ, когда отношения заканчиваются; текущие бронирования рассчитываются по действующим на тот момент условиям.' },
      ],
    },
    related: ['sale-channels', 'partner-access', 'markup', 'companies'],
  },

  'sign-access': {
    title: 'Доступ к SIGN',
    tag: 'Предоставление wholesale',
    tagline: 'Позволение партнёрскому агентству выписывать билеты под вашим SIGN.',
    business: {
      lead: 'Доступ к SIGN одалживает выписной кредентиал (SIGN) другому агентству, чтобы оно могло оформлять поставщика, на которого не держит кредентиалы. Владелец SIGN предоставляет доступ, задаёт наценку в сторону этого получателя и может отозвать его — это предоставление за слоем ценообразования Wholesale.',
      points: [
        { title: 'Выписка на заимствованных кредентиалах', desc: 'Получатель выписывает билеты под SIGN владельца без регистрации собственного кредентиала BSP/IATA.' },
        { title: 'Наценка в сторону получателя', desc: 'Владелец задаёт наценку wholesale по каждому предоставлению — свою маржу за одалживание SIGN.' },
        { title: 'Двустороннее согласие', desc: 'Предоставление предлагается и принимается и может быть отозвано; совместное использование это разрешение, а не передача.' },
      ],
    },
    software: {
      lead: 'Журнал предоставлений SIGN — выданных и полученных.',
      points: [
        { title: 'Предоставить и задать наценку', desc: 'Предложите SIGN партнёру с необязательным переопределением наценки и окном действия.' },
        { title: 'Принять / отклонить', desc: 'Получатель принимает, чтобы начать выписку на SIGN, или отклоняет.' },
        { title: 'Питает правила Wholesale', desc: 'Наценка предоставления — это унаследованный запасной вариант; явные правила Wholesale в Наценках и правилах (область AGENCY / SIGN) переопределяют её.' },
      ],
    },
    related: ['signs', 'partner-access', 'markup', 'agencies'],
  },

  signs: {
    title: 'SIGN',
    tag: 'Выписные кредентиалы',
    tagline: 'Кредентиалы BSP / IATA / CASS, которые агентство использует для выписки билетов.',
    business: {
      lead: 'SIGN — это зарегистрированный кредентиал, который позволяет агентству выписывать билеты против конкретного поставщика. Это актив в сердце слоя Wholesale: владелец SIGN может выписывать напрямую или одалживать SIGN партнёрам (доступ к SIGN) и делать наценку на то, что они выписывают.',
      points: [
        { title: 'Право выписывать', desc: 'Без SIGN — или заимствованного доступа к нему — агентство может продавать, но не выписывать против этого поставщика.' },
        { title: 'Один активный на поставщика', desc: 'Агентство держит один активный SIGN на поставщика; заменённые уходят на покой, а не удаляются.' },
        { title: 'Актив wholesale', desc: 'Одалживание SIGN и наценка на выписку получателя — это бизнес слоя Wholesale.' },
      ],
    },
    software: {
      lead: 'Реестр SIGN агентства с их поставщиком, кодом, эмитентом и сроком действия.',
      points: [
        { title: 'Зарегистрировать SIGN', desc: 'Запишите поставщика, код SIGN, эмитента и счёт BSP с окном действия.' },
        { title: 'Принадлежит агентству', desc: 'SIGN всегда принадлежат агентству (компании владеют каналами, а не SIGN).' },
        { title: 'Делиться через доступ к SIGN', desc: 'Одолжите SIGN партнёру из доступа к SIGN; отзовите, когда отношения заканчиваются.' },
      ],
    },
    related: ['sign-access', 'partner-access', 'markup', 'agencies'],
  },

  branches: {
    title: 'Филиалы',
    tag: 'Оргструктура',
    tagline: 'Субофисы агентства, используемые для задания области сотрудников и бронирований.',
    business: {
      lead: 'Филиалы моделируют физические или организационные субофисы агентства. Они позволяют более крупному агентству организовать своих людей и относить бронирования и результаты к офису, который их сделал, не разделяясь на отдельные агентства.',
      points: [
        { title: 'Атрибуция', desc: 'Бронирования и выручка могут отчитываться по каждому филиалу, чтобы вы видели, какой офис работает.' },
        { title: 'Область сотрудников', desc: 'Пользователи назначаются в филиал, удерживая их вид и права в рамках их офиса.' },
        { title: 'Одно агентство, много офисов', desc: 'Филиалы сохраняют многоофисное агентство как единые торговые отношения.' },
      ],
    },
    software: {
      lead: 'Каталог филиалов агентства.',
      points: [
        { title: 'Создать и редактировать', desc: 'Добавляйте филиалы с их названием, контактом и данными о местоположении.' },
        { title: 'Назначить сотрудников', desc: 'Филиал используется при выделении пользователей и ролей под агентством.' },
        { title: 'Вид админа vs агентства', desc: 'Админ управляет филиалами любого агентства; агентство управляет своими.' },
      ],
    },
    related: ['agencies', 'agency-profile'],
  },

  'agency-groups': {
    title: 'Группы агентств',
    tag: 'Сегментация',
    tagline: 'Именованные корзины агентств, которые вы можете нацелить одним правилом ценообразования.',
    business: {
      lead: 'Группы агентств — это корзины сегментации («Специалисты по Умре», «Тегеранские корпоративные»), которые позволяют назначать цену целому набору агентств вместе, а не по одному. Они питают область партнёра GROUP в движке ценообразования.',
      points: [
        { title: 'Цена по сегменту', desc: 'Группа — это цель для правила Наценок и правил с областью партнёра = Group.' },
        { title: 'Членство многие-ко-многим', desc: 'Агентство может входить в несколько групп; группа держит много агентств.' },
        { title: 'Область по владельцу', desc: 'Группа либо общая для платформы, либо принадлежит агентству и следует жизненному циклу статуса (никогда не удаляется жёстко).' },
      ],
    },
    software: {
      lead: 'Поверхность управления группами и их членствами.',
      points: [
        { title: 'Создать и курировать', desc: 'Определите группу и добавляйте или удаляйте агентства-участники.' },
        { title: 'Нацелить в ценообразовании', desc: 'Группа появляется в селекторе области партнёра Group редактора правил.' },
        { title: 'Жизненный цикл, а не удаление', desc: 'Отправьте группу на покой по статусу, чтобы существующие правила, ссылающиеся на неё, оставались разрешимыми.' },
      ],
    },
    related: ['markup', 'agencies'],
  },
};
